import { useCallback, useState } from "react";
import { useGlobalSearchParams } from "expo-router";
import { useQueryClient } from "@tanstack/react-query";
import type { SubmitHandler } from "react-hook-form";

import { api } from "~/utils/api";
import img from "~/assets/signup/signup-2.png";
import type { SignupFormData } from "~/component/feature/auth/signup-form";
import { SignupForm } from "~/component/feature/auth/signup-form";
import {
  AuthContainer,
  AuthContent,
  AuthImage,
  AuthSlugWrapper,
} from "~/component/feature/auth/wrappers";
import { track } from "~/hooks/use-track";

export default function SignupSlugPage() {
  const { slug } = useGlobalSearchParams<{ slug: string }>();
  const [isSuccess, setIsSuccess] = useState(false);
  const apiUtils = api.useUtils();
  const queryClient = useQueryClient();

  const { isError } = api.authRouter.checkSlug.useQuery(
    { slug },
    {
      refetchInterval: false,
      refetchIntervalInBackground: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );

  const {
    mutate,
    error: mutationError,
    isError: mutationIsError,
  } = api.authRouter.signupBySlug.useMutation({
    onMutate: () => track("Submit Signup form"),
    onSuccess: async () => {
      queryClient.clear();
      await apiUtils.context.person.fetch();
      track("Submit Signup form success");
      setIsSuccess(true);
    },
  });

  const onSubmit: SubmitHandler<SignupFormData> = useCallback(
    (data) => mutate({ ...data, slug }),
    [mutate, slug],
  );

  return (
    <AuthContainer>
      <AuthContent>
        <AuthSlugWrapper isSuccess={isSuccess} isError={isError}>
          <SignupForm
            withGoBack
            onSubmit={onSubmit}
            isError={mutationIsError}
            errorMessage={mutationError?.message}
          />
        </AuthSlugWrapper>
      </AuthContent>

      <AuthImage src={img} />
    </AuthContainer>
  );
}
